import { useState } from "react";
import { Search, Building2, User as UserIcon } from "lucide-react";
import { toast } from "sonner";
import { Button, Card, CardContent, Input } from "@bookdental/ui";
import { trpc } from "@/lib/trpc";
import { cn } from "@/lib/cn";
import { ErroConsulta } from "@/components/ErroConsulta";

const PAGE_SIZE = 20;

const TIPO_LABEL: Record<string, string> = {
  DENTAL_OFFICE: "Consultório",
  DENTAL_LAB: "Laboratório",
  RADIOLOGY_CENTER: "Radiologia",
};

type OrgRow = {
  id: string;
  name: string;
  slug: string;
  types: string[];
  isPersonal: boolean;
  plan: "FREE" | "PAID";
  createdAt: string | Date;
  membros: number;
  pacientes: number;
};

function LinhaOrganizacao({ org }: { org: OrgRow }) {
  const utils = trpc.useUtils();
  const setPlan = trpc.admin.organizations.setPlan.useMutation({
    onSuccess: () => {
      toast.success(org.plan === "FREE" ? "Organização marcada como paga" : "Organização marcada como gratuita");
      utils.admin.organizations.list.invalidate();
    },
    onError: (err) => toast.error(err.message || "Falha ao alterar plano"),
  });

  return (
    <div className="flex items-center gap-4 border-b border-admin-200 p-4 last:border-b-0">
      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-admin-100 text-admin-900">
        {org.isPersonal ? <UserIcon className="h-5 w-5" /> : <Building2 className="h-5 w-5" />}
      </span>

      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <p className="truncate font-semibold text-admin-900">{org.name}</p>
          {org.isPersonal && (
            <span className="rounded bg-admin-100 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-admin-700">
              Pessoal
            </span>
          )}
        </div>
        <p className="truncate text-xs text-admin-700">/{org.slug}</p>
      </div>

      <div className="hidden gap-1 sm:flex">
        {org.types.map((t) => (
          <span
            key={t}
            className="rounded-md border border-admin-200 px-2 py-0.5 text-xs text-admin-700"
          >
            {TIPO_LABEL[t] ?? t}
          </span>
        ))}
      </div>

      <button
        type="button"
        onClick={() => setPlan.mutate({ id: org.id, plan: org.plan === "PAID" ? "FREE" : "PAID" })}
        disabled={setPlan.isPending}
        title="Sem billing real ainda — toggle manual pra testar features de plano pago (issues #6/#7)"
        className={cn(
          "w-20 shrink-0 rounded-md px-2 py-1 text-center text-[11px] font-bold uppercase tracking-wider transition-colors disabled:opacity-50",
          org.plan === "PAID"
            ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-200"
            : "bg-admin-100 text-admin-700 hover:bg-admin-200",
        )}
      >
        {org.plan === "PAID" ? "Pago" : "Grátis"}
      </button>

      <div className="w-20 text-right text-sm text-admin-700">
        {org.membros} {org.membros === 1 ? "membro" : "membros"}
      </div>
      <div className="w-24 text-right text-sm text-admin-700">
        {org.pacientes} {org.pacientes === 1 ? "paciente" : "pacientes"}
      </div>
      <div className="hidden w-24 text-right text-xs text-admin-700/70 md:block">
        {new Date(org.createdAt).toLocaleDateString("pt-BR")}
      </div>
    </div>
  );
}

export function AdminOrganizations() {
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);

  const { data, isLoading, error } = trpc.admin.organizations.list.useQuery({
    search: search.trim() || undefined,
    take: PAGE_SIZE,
    skip: page * PAGE_SIZE,
  });

  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-admin-900">Organizações</h1>
        <p className="mt-1 text-sm text-admin-700">
          Todas as organizações da plataforma — consultórios, laboratórios, centros de radiologia
          e espaços pessoais.
        </p>
      </div>

      <div className="flex items-center gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-admin-700/50" />
          <Input
            placeholder="Buscar por nome ou identificador..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(0);
            }}
            className="border-admin-200 bg-white pl-9"
          />
        </div>
        <div className="text-sm text-admin-700">
          {isLoading ? "..." : `${total} resultado${total === 1 ? "" : "s"}`}
        </div>
      </div>

      <Card className="border-admin-200">
        <CardContent className="p-0">
          {error ? (
            <div className="p-5">
              <ErroConsulta error={error} />
            </div>
          ) : isLoading ? (
            <div className="flex justify-center py-16">
              <div className="h-8 w-8 animate-spin rounded-full border-2 border-admin-900 border-t-transparent" />
            </div>
          ) : data?.rows.length === 0 ? (
            <div className="py-16 text-center text-sm text-admin-700">
              Nenhuma organização encontrada.
            </div>
          ) : (
            <div>
              {data?.rows.map((org: OrgRow) => (
                <LinhaOrganizacao key={org.id} org={org} />
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {total > PAGE_SIZE && (
        <div className="flex items-center justify-between text-sm">
          <span className="text-admin-700">
            Página {page + 1} de {totalPages}
          </span>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={page === 0}
            >
              Anterior
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage((p) => p + 1)}
              disabled={page + 1 >= totalPages}
            >
              Próxima
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
